import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import communityService from '../../../services/community/communityService';
import Community from '../../../types/community';
import ModalLayout from '../layouts/ModalLayout';
import CancelSaveButton from '../misc/CancelSaveButton';

interface DeleteCommunityModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  community?: Pick<Community, 'id' | 'name'>;
  onDelete?: () => Promise<void>;
  redirectTo?: string;
}

export default function DeleteCommunityModal({
  open,
  setOpen,
  community: communityProp,
  onDelete,
  redirectTo = '/',
}: DeleteCommunityModalProps): JSX.Element {
  const navigate = useNavigate();
  const [inputCheck, setInputCheck] = useState<string>('');
  const selectedCommunity = useSelectedCommunity();
  const community = communityProp ?? selectedCommunity;
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) {
      setInputCheck('');
    }
  }, [open]);

  const handleClick = async (): Promise<void> => {
    if (inputCheck !== community?.name) {
      toast.error(t('toast.error.wrongName'));
      return;
    }

    setOpen(false);
    try {
      if (onDelete) {
        await onDelete();
      } else {
        await communityService.deleteCommunity();
      }
      toast.success(t('toast.success.deletedCommunity'));
      navigate(redirectTo);
    } catch (error) {
      console.error('Failed to delete community:', error);
      toast.error(t('toast.error.deleteCommunity'));
    }
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      className="h-a z-50 mx-6 w-fit min-w-[400px] max-w-[700px] rounded-[12px] bg-secondary-50 px-10 py-6 pb-4 pt-5">
      <div>
        <h2 className="mb-2 text-3xl font-semibold">{t('component.modal.deleteCommunity.title')}</h2>
        <p className="mb-2">{t('component.modal.deleteCommunity.subtitle')}</p>
        <ul data-testid="delete-list" className="mb-4 list-inside list-disc text-primary-900">
          <li>{t('component.modal.deleteCommunity.point1')}</li>
          <li>{t('component.modal.deleteCommunity.point2')}</li>
        </ul>
        <p className="mb-5">
          {t('component.modal.deleteCommunity.confirm', { name: community?.name })}
        </p>
        <input
          type="text"
          placeholder={community?.name}
          className="mb-5 flex rounded border border-gray-300 px-4 py-2"
          value={inputCheck}
          onChange={(e): void => setInputCheck(e.target.value)}
        />
      </div>

      <div className="mt-4 flex w-full justify-end">
        <CancelSaveButton
          onCancel={() => setOpen(false)}
          onSave={handleClick}
          disabled={inputCheck !== community?.name}
          buttonText={t('general.delete')}
        />
      </div>
    </ModalLayout>
  );
}
